/**
 * JavaScript Loading Utility
 * Provides functions to load JavaScript libraries asynchronously to improve initial page load performance
 */

/**
 * Load a JavaScript file asynchronously
 * @param {string} src - The JavaScript file URL
 * @param {string} type - Script type (default: 'text/javascript')
 * @returns {Promise} - Promise that resolves when script is loaded
 */
export function loadJS(src, type = 'text/javascript') {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      resolve(existingScript);
      return;
    }

    const script = document.createElement('script');
    script.type = type;
    script.src = src;
    script.async = true;
    
    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error(`Failed to load JavaScript: ${src}`));
    
    document.head.appendChild(script);
  });
}

/**
 * Load multiple JavaScript files asynchronously
 * @param {Array} jsFiles - Array of JavaScript file objects {src, type}
 * @returns {Promise} - Promise that resolves when all scripts are loaded
 */
export function loadMultipleJS(jsFiles) {
  const promises = jsFiles.map(js => loadJS(js.src, js.type));
  return Promise.all(promises);
}

/**
 * Load JavaScript with defer attribute
 * @param {string} src - The JavaScript file URL
 * @returns {Promise} - Promise that resolves when script is loaded
 */
export function loadDeferredJS(src) {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      resolve(existingScript);
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.defer = true;
    
    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error(`Failed to load JavaScript: ${src}`));
    
    document.head.appendChild(script);
  });
}

/**
 * Preload JavaScript file (hint to browser to fetch but don't execute)
 * @param {string} src - The JavaScript file URL
 */
export function preloadJS(src) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'script';
  link.href = src;
  document.head.appendChild(link);
}

/**
 * Load JavaScript from node_modules path
 * @param {string} modulePath - Path to module in node_modules
 * @returns {Promise} - Promise that resolves when script is loaded
 */
export function loadModuleJS(modulePath) {
  // In development, we need to load from the dev server
  // In production, this would be bundled
  const src = process.env.NODE_ENV === 'development' 
    ? `/node_modules/${modulePath}`
    : modulePath;
  
  return loadJS(src);
}
